/**
 * Size System
 * CSS size values with branded types for type safety
 */

// Branded types for different size units
declare const __size_percent_brand: unique symbol;
declare const __size_px_brand: unique symbol;
declare const __size_rem_brand: unique symbol;
declare const __size_em_brand: unique symbol;
declare const __size_vh_brand: unique symbol;
declare const __size_vw_brand: unique symbol;
declare const __size_auto_brand: unique symbol;

export interface SizePercentToken {
  readonly [__size_percent_brand]: true;
  readonly _value: string;
}

export interface SizePxToken {
  readonly [__size_px_brand]: true;
  readonly _value: string;
}

export interface SizeRemToken {
  readonly [__size_rem_brand]: true;
  readonly _value: string;
}

export interface SizeEmToken {
  readonly [__size_em_brand]: true;
  readonly _value: string;
}

export interface SizeVhToken {
  readonly [__size_vh_brand]: true;
  readonly _value: string;
}

export interface SizeVwToken {
  readonly [__size_vw_brand]: true;
  readonly _value: string;
}

export interface SizeAutoToken {
  readonly [__size_auto_brand]: true;
  readonly _value: string;
}

// Union type for all size tokens
export type SizeToken =
  | SizePercentToken
  | SizePxToken
  | SizeRemToken
  | SizeEmToken
  | SizeVhToken
  | SizeVwToken
  | SizeAutoToken;

// Factory functions
const createSizePercentToken = (value: number): SizePercentToken => ({ _value: `${value}%` } as SizePercentToken);

const createSizePxToken = (value: number): SizePxToken => ({ _value: `${value}px` } as SizePxToken);

const createSizeRemToken = (value: number): SizeRemToken => ({ _value: `${value}rem` } as SizeRemToken);

const createSizeEmToken = (value: number): SizeEmToken => ({ _value: `${value}em` } as SizeEmToken);

const createSizeVhToken = (value: number): SizeVhToken => ({ _value: `${value}vh` } as SizeVhToken);

const createSizeVwToken = (value: number): SizeVwToken => ({ _value: `${value}vw` } as SizeVwToken);

const createSizeAutoToken = (): SizeAutoToken => ({ _value: "auto" } as SizeAutoToken);

// Resolver function for size tokens
export const resolveSize = (token: SizeToken): string => token._value;

// Public API
export const Size = {
  // Percentage values
  percent: (value: number) => createSizePercentToken(value),

  // Pixel values
  px: (value: number) => createSizePxToken(value),

  // Rem values
  rem: (value: number) => createSizeRemToken(value),

  // Em values
  em: (value: number) => createSizeEmToken(value),

  // Viewport height
  vh: (value: number) => createSizeVhToken(value),

  // Viewport width
  vw: (value: number) => createSizeVwToken(value),

  // Auto value
  auto: createSizeAutoToken(),

  // Common presets
  full: createSizePercentToken(100),
  half: createSizePercentToken(50),
  third: createSizePercentToken(33.333333),
  quarter: createSizePercentToken(25),
  fifth: createSizePercentToken(20),

  // Viewport presets
  fullHeight: createSizeVhToken(100),
  fullWidth: createSizeVwToken(100),
  halfHeight: createSizeVhToken(50),
  halfWidth: createSizeVwToken(50),
} as const;
